import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePollRegistrationStatus } from "@/infra/apis/usePollRegistrationStatus";
import { pushGtmEvent } from "@/infra/observability/googleTagManager";
import { parsePriceString } from "@/infra/observability/parsePriceString";
import { AppRoutes } from "@/infra/router";
import { webshopEnvironment } from "@akina/config";
import { DEFAULT_LOCALE, WebsiteLocaleMapping } from "@akina/infra";
import { LoadingOverlay, useLocaleStore, useCmsCopyStore } from "@akina/presentation";
import type { WebshopTransactionStatusEnum } from "types/schemas";

const TIMEOUT_MS = 30_000;
const FAILED_PAYMENT_STATUSES: Set<WebshopTransactionStatusEnum> = new Set(["failed", "canceled", "expired"]);

export function PaymentCompletedPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const locale = useLocaleStore(s => s.locale);
  const navigate = useNavigate();
  const websiteUrl = webshopEnvironment.websiteUrl;

  const { data, isError, hasExceededPollLimit } = usePollRegistrationStatus(token);

  useEffect(() => {
    if (!token) {
      navigate(`${AppRoutes.Checkout}?paymentFailed=true`, { replace: true });
    }
  }, [navigate, token]);

  useEffect(() => {
    if (data?.paymentStatus === "paid") {
      if (data.inviteId) {
        const priceString =
          useCmsCopyStore.getState().cmsCopy?.localeCopy?.["checkoutPage.paymentStep.productCard.totalPrice"];
        pushGtmEvent("payment_completed", {
          transaction_id: data.inviteId,
          transaction_value: parsePriceString(priceString),
        });
        const safeLocalePrefix = WebsiteLocaleMapping[locale] ?? WebsiteLocaleMapping[DEFAULT_LOCALE];
        const successUrl = new URL(`${safeLocalePrefix}/checkout-success/`, websiteUrl);
        successUrl.searchParams.set("invite_code", data.inviteId);
        globalThis.location.href = successUrl.toString();
      } else {
        navigate(`${AppRoutes.Checkout}?paymentFailed=true`, { replace: true });
      }
    }
    if ((data?.paymentStatus && FAILED_PAYMENT_STATUSES.has(data.paymentStatus)) || isError || hasExceededPollLimit) {
      navigate(`${AppRoutes.Checkout}?paymentFailed=true`, { replace: true });
    }
  }, [data, hasExceededPollLimit, isError, locale, navigate, websiteUrl]);

  useEffect(() => {
    if (!token) return;
    const timer = setTimeout(() => {
      navigate(`${AppRoutes.Checkout}?paymentFailed=true`, { replace: true });
    }, TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [navigate, token]);

  return <LoadingOverlay />;
}
